import {
  AccountCreate,
  AccountResponse,
  AppAvailablePackagingListParams,
  AppAvailableProcessingListParams,
  AppAvailableTransportListParams,
  AppBaseFoodsListParams,
  AppIngredientPickerListParams,
  AvailableIngredients,
  AvailablePackaging,
  AvailableProcessing,
  AvailableTransport,
  CollectionDetail,
  CollectionList,
  CompanyReportsSitesElectricitySuppliesListParams,
  CompanyReportsSitesListParams,
  CountriesListParams,
  Country,
  ElectricitySupply,
  IngredientInsightsList,
  IngredientPickerItem,
  PaginatedElectricitySupplyList,
  PaginatedSiteList,
  ProcessingTypeList,
  ReportOrganizationFoodDetail,
  ReportProductDetail,
  Site,
  TokenRefresh,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';
import { useAxiosConfig } from './AxiosConfigProvider';
import {
  getAppAvailablePackagingListQueryKey,
  getAppAvailableProcessingListQueryKey,
  getAppAvailableTransportListQueryKey,
  getAppCollectionsListQueryKey,
  getAppCollectionsRetrieveQueryKey,
  useAppAccountCompleteSignupCreate,
  useAppAccountCreate,
  useAppAccountResendEmailVerificationCreate,
  useAppAccountRetrieve,
  useAppAccountVerifyEmailCreate,
  useAppAvailablePackagingList,
  useAppAvailableProcessingList,
  useAppAvailableTransportList,
  useAppBaseFoodsList,
  useAppCollectionsCreate,
  useAppCollectionsDestroy,
  useAppCollectionsList,
  useAppCollectionsRetrieve,
  useAppCollectionsUpdate,
  useAppFoodsInsightsList,
  useAppHomeRetrieve,
  useAppIngredientPickerList,
  useAppLoginCreate,
  useAppLogoutCreate,
  useAppPackagingCreate,
  useAppPackagingFileCreate,
  useAppPackagingList,
  useAppProcessingCreate,
  useAppProcessingFileCreate,
  useAppProcessingList,
  useAppProductsAssessCreate,
  useAppReportsList,
  useAppReportsProductionRetrieve,
  useAppReportsPurchasingRetrieve,
  useAppReportsSalesRetrieve,
  useAppTokenRefresh2Create,
  useAppTransportCreate,
  useAppTransportFileCreate,
  useAppTransportList,
  useAppUnitsList,
} from './lib/app/app';
import {
  getCompanyReportsSitesElectricitySuppliesListQueryKey,
  getCompanyReportsSitesListQueryKey,
  useCompanyReportsSitesBulkCreate,
  useCompanyReportsSitesCreate,
  useCompanyReportsSitesElectricitySuppliesCreate,
  useCompanyReportsSitesElectricitySuppliesList,
  useCompanyReportsSitesElectricitySuppliesRetrieve,
  useCompanyReportsSitesElectricitySuppliesUpdate,
  useCompanyReportsSitesList,
  useCompanyReportsSitesRetrieve,
  useCompanyReportsSitesUpdate,
} from './lib/company-reports/company-reports';
import { useCountriesList } from './lib/countries/countries';

export const useLogin = () => useAppLoginCreate({ request: useAxiosConfig() });

export const useLogout = () =>
  useAppLogoutCreate({ request: useAxiosConfig() });

export const useRefreshToken = () =>
  useAppTokenRefresh2Create<Promise<TokenRefresh>>({
    request: useAxiosConfig(),
  });

export const useVerifyEmail = () =>
  useAppAccountVerifyEmailCreate<Promise<void>>({
    request: useAxiosConfig(),
  });

export const useSendEmailVerification = () =>
  useAppAccountResendEmailVerificationCreate<Promise<void>>({
    request: useAxiosConfig(),
  });

export const useAccount = (enabled: boolean) => {
  return useAppAccountRetrieve<AccountResponse>({
    request: useAxiosConfig(),
    query: {
      enabled,
    },
  });
};

export const useAccountCreate = () =>
  useAppAccountCreate<AccountCreate>({
    request: useAxiosConfig(),
  });

export const useGetUnitList = () =>
  useAppUnitsList({
    request: useAxiosConfig(),
    query: {
      staleTime: Infinity,
    },
  });

export const useAssessDemoProduct = () =>
  useAppProductsAssessCreate({
    request: useAxiosConfig(),
  });

export const useGetDashboard = () =>
  useAppHomeRetrieve({
    request: useAxiosConfig(),
  });

export const useGetReports = () => {
  return useAppReportsList({
    request: useAxiosConfig(),
  });
};

export const useGetPurchasingReport = (reportUuid: string) => {
  return useAppReportsPurchasingRetrieve<ReportOrganizationFoodDetail>(
    reportUuid,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useGetSalesReport = (reportUuid: string) => {
  return useAppReportsSalesRetrieve<ReportProductDetail>(reportUuid, {
    request: useAxiosConfig(),
  });
};

export const useGetProductionReport = (reportUuid: string) => {
  return useAppReportsProductionRetrieve<ReportProductDetail>(reportUuid, {
    request: useAxiosConfig(),
  });
};

export const useGetCollections = () => {
  return useAppCollectionsList<CollectionList[]>({
    request: useAxiosConfig(),
  });
};

export const useGetCollection = (collectionUuid: string) =>
  useAppCollectionsRetrieve<CollectionDetail>(collectionUuid, {
    request: useAxiosConfig(),
    query: {
      enabled: !!collectionUuid,
    },
  });

export const useCreateCollection = () => {
  const queryClient = useQueryClient();
  return useAppCollectionsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getAppCollectionsListQueryKey());
      },
    },
  });
};

export const useEditCollection = () => {
  const queryClient = useQueryClient();
  return useAppCollectionsUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries([
          getAppCollectionsListQueryKey(),
          getAppCollectionsRetrieveQueryKey(data.uuid),
        ]);

        queryClient.removeQueries(getAppCollectionsRetrieveQueryKey(data.uuid));
      },
    },
  });
};

export const useDeleteCollection = () => {
  const queryClient = useQueryClient();
  return useAppCollectionsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getAppCollectionsListQueryKey());
      },
    },
  });
};

export const useGetAvailableIngredients = (params?: AppBaseFoodsListParams) =>
  useAppBaseFoodsList<AvailableIngredients>(params, {
    request: useAxiosConfig(),
    query: {
      staleTime: 1000 * 60 * 60 * 24,
    },
  });

// TEMP
export const useGetAvailableIngredientsV2 = (
  params?: AppIngredientPickerListParams
) =>
  useAppIngredientPickerList<IngredientPickerItem[]>(params, {
    request: useAxiosConfig(),
    query: {
      staleTime: 1000 * 60 * 60 * 24,
    },
  });

export const useGetAvailablePackaging = (
  params?: AppAvailablePackagingListParams
) =>
  useAppAvailablePackagingList<AvailablePackaging>(params, {
    request: useAxiosConfig(),
    query: {
      staleTime: 1000 * 60 * 60 * 24,
    },
  });

export const useGetAvailableTransport = (
  params?: AppAvailableTransportListParams
) => {
  return useAppAvailableTransportList<AvailableTransport>(params, {
    request: useAxiosConfig(),
  });
};

export const useGetAvailableProcessing = (
  params?: AppAvailableProcessingListParams
) => {
  return useAppAvailableProcessingList<AvailableProcessing>(params, {
    request: useAxiosConfig(),
  });
};

export const useGetIngredientsWithInsights = () => {
  return useAppFoodsInsightsList<IngredientInsightsList[]>({
    request: useAxiosConfig(),
  });
};

export const useCreatePackaging = () => {
  return useAppPackagingCreate({
    request: useAxiosConfig(),
  });
};

export const useCreateProcessing = () => {
  return useAppProcessingCreate({
    request: useAxiosConfig(),
  });
};

export const useCreateTransport = () => {
  return useAppTransportCreate({
    request: useAxiosConfig(),
  });
};

export const usePackagingFileCreate = () => {
  const queryClient = useQueryClient();
  return useAppPackagingFileCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getAppAvailablePackagingListQueryKey());
      },
    },
  });
};

export const useProcessingFileCreate = () => {
  const queryClient = useQueryClient();
  return useAppProcessingFileCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getAppAvailableProcessingListQueryKey());
      },
    },
  });
};

export const useTransportFileCreate = () => {
  const queryClient = useQueryClient();
  return useAppTransportFileCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getAppAvailableTransportListQueryKey());
      },
    },
  });
};

export const useGetPackaging = () =>
  useAppPackagingList({
    request: useAxiosConfig(),
  });

export const useGetTransport = () =>
  useAppTransportList({
    request: useAxiosConfig(),
  });

export const useGetProcessing = () =>
  useAppProcessingList<ProcessingTypeList[]>({
    request: useAxiosConfig(),
  });

export const useGetCountries = (params?: CountriesListParams) =>
  useCountriesList<Country[]>(params, {
    request: useAxiosConfig(),
    query: {
      staleTime: Infinity,
    },
  });

export const useCompleteSignup = () => {
  const queryClient = useQueryClient();

  return useAppAccountCompleteSignupCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(['account']);
      },
    },
  });
};

export const useGetSites = (params: CompanyReportsSitesListParams) => {
  return useCompanyReportsSitesList<PaginatedSiteList>(params, {
    request: useAxiosConfig(),
    query: {
      refetchOnWindowFocus: true,
    },
  });
};

export const useGetSite = (siteUuid?: string) => {
  return useCompanyReportsSitesRetrieve<Site>(siteUuid!, {
    request: useAxiosConfig(),
    query: {
      enabled: !!siteUuid,
    },
  });
};

export const useCreateSite = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsSitesCreate<Site>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getCompanyReportsSitesListQueryKey());
      },
    },
  });
};

export const useCreateSites = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsSitesBulkCreate<Site[]>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getCompanyReportsSitesListQueryKey());
      },
    },
  });
};

export const useUpdateSite = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsSitesUpdate<Site>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getCompanyReportsSitesListQueryKey());
      },
    },
  });
};

export const useGetElectricitySupplies = (
  params: CompanyReportsSitesElectricitySuppliesListParams
) => {
  return useCompanyReportsSitesElectricitySuppliesList<PaginatedElectricitySupplyList>(
    params,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useGetElectricitySupply = (supplyUuid?: string) => {
  return useCompanyReportsSitesElectricitySuppliesRetrieve<ElectricitySupply>(
    supplyUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!supplyUuid,
      },
    }
  );
};

export const useCreateElectricitySupply = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsSitesElectricitySuppliesCreate<ElectricitySupply>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(
          getCompanyReportsSitesElectricitySuppliesListQueryKey()
        );
      },
    },
  });
};

export const useUpdateElectricitySupply = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsSitesElectricitySuppliesUpdate<ElectricitySupply>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(
          getCompanyReportsSitesElectricitySuppliesListQueryKey()
        );
      },
    },
  });
};
